import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { BudgetPlanItemInput, BudgetPlanRow, BudgetPlanSaveRequest, BudgetPlanStatus } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { formatAmount, formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';
import { useAuthStore } from '../stores/auth';

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'default' },
  SUBMITTED: { text: '待批复', color: 'gold' },
  APPROVING: { text: '批复中', color: 'gold' },
  APPROVED: { text: '已批复', color: 'blue' },
  RELEASED: { text: '已下达', color: 'green' },
  RETURNED: { text: '已退回', color: 'red' },
  REJECTED: { text: '已驳回', color: 'red' }
};

const statusTag = (value: string): ReactElement => {
  const meta = STATUS_META[value] ?? { text: value, color: 'default' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
};

type StatusFilter = 'ALL' | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'RELEASED';

interface DraftItem extends BudgetPlanItemInput {
  key: string;
}

interface PlanDraft {
  budgetYear: number;
  planType: 'ANNUAL' | 'ADJUST';
  remark: string;
  items: DraftItem[];
}

const emptyItem = (): DraftItem => ({
  key: String(Date.now() + Math.random()),
  deptId: '',
  deptName: '',
  categoryId: '',
  categoryName: '',
  materialId: '',
  materialName: '',
  lastYearAmount: 0,
  applyAmount: 0,
  calcBasis: ''
});

/** 预算编制（YS 单）：自下而上填报 → 提交 → 批复（可整体调减）→ 下达为正式预算。 */
export default function BudgetPlan() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const canManage =
    profile?.level === 'admin' || hasPermission(profile?.perms, 'wl:settle:manage') || hasPermission(profile?.perms, 'wl:apply:approve');

  const [year, setYear] = useState<number>(dayjs().year());
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [editing, setEditing] = useState<BudgetPlanRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<PlanDraft>({
    budgetYear: dayjs().year(),
    planType: 'ANNUAL',
    remark: '',
    items: [emptyItem()]
  });
  const [approveTarget, setApproveTarget] = useState<BudgetPlanRow | null>(null);
  const [approveAmount, setApproveAmount] = useState<number>(0);
  const [approveOpinion, setApproveOpinion] = useState('');

  const list = useQuery({
    queryKey: ['budget', 'plan', year, status],
    placeholderData: keepPreviousData,
    queryFn: () =>
      hq.billing.budget.plans.list({
        size: PAGE_SIZE,
        budgetYear: year,
        status: status === 'ALL' ? undefined : (status as BudgetPlanStatus)
      })
  });

  const depts = useQuery({
    queryKey: ['orgStructure', 'depts', 'budget-plan'],
    queryFn: () => hq.orgStructure.dept.list({}),
    staleTime: 5 * 60 * 1000
  });

  const categories = useQuery({
    queryKey: ['catalog', 'categories', 'budget-plan'],
    queryFn: () => hq.catalog.categories({}),
    staleTime: 5 * 60 * 1000
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['budget'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (draft.items.some((item) => !item.deptId)) throw new Error('每行明细都必须选择科室');
      if (draft.items.every((item) => (item.applyAmount ?? 0) <= 0)) throw new Error('至少一行申报金额需大于 0');
      const body: BudgetPlanSaveRequest = {
        budgetYear: draft.budgetYear,
        planType: draft.planType,
        remark: draft.remark || undefined,
        items: draft.items.map((item) => ({
          deptId: item.deptId,
          deptName: item.deptName,
          categoryId: item.categoryId || '0',
          categoryName: item.categoryName,
          materialId: item.materialId || '0',
          materialName: item.materialName,
          lastYearAmount: item.lastYearAmount ?? 0,
          applyAmount: item.applyAmount ?? 0,
          calcBasis: item.calcBasis
        }))
      };
      return editing
        ? hq.billing.budget.plans.update(editing.id, body)
        : hq.billing.budget.plans.create(body);
    },
    onSuccess: async () => {
      message.success(editing ? '预算编制已保存' : '预算编制已创建');
      setDrawerOpen(false);
      setEditing(null);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const submit = useMutation({
    mutationFn: (id: string) => hq.billing.budget.plans.submit(id),
    onSuccess: async () => {
      message.success('已提交，等待上级批复');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '提交失败')
  });

  const approve = useMutation({
    mutationFn: () =>
      hq.billing.budget.plans.approve(approveTarget?.id ?? '', {
        approvedAmount: approveAmount,
        opinion: approveOpinion || undefined
      }),
    onSuccess: async (result) => {
      message.success(`已批复 ${formatAmount(result.approvedAmount)}`);
      setApproveTarget(null);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '批复失败')
  });

  const reject = useMutation({
    mutationFn: ({ id, opinion }: { id: string; opinion: string }) =>
      hq.billing.budget.plans.reject(id, { opinion }),
    onSuccess: async () => {
      message.success('已退回修改');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '退回失败')
  });

  const release = useMutation({
    mutationFn: (id: string) => hq.billing.budget.plans.release(id),
    onSuccess: async () => {
      message.success('已下达为正式预算（预算执行页可见）');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '下达失败')
  });

  const remove = useMutation({
    mutationFn: (id: string) => hq.billing.budget.plans.remove(id),
    onSuccess: async () => {
      message.success('已删除');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败')
  });

  const openCreate = () => {
    setEditing(null);
    setDraft({ budgetYear: year, planType: 'ANNUAL', remark: '', items: [emptyItem()] });
    setDrawerOpen(true);
  };

  const openEdit = async (row: BudgetPlanRow) => {
    const detail = await hq.billing.budget.plans.detail(row.id);
    setEditing(row);
    setDraft({
      budgetYear: detail.plan.budgetYear,
      planType: (detail.plan.planType === 'ADJUST' ? 'ADJUST' : 'ANNUAL') as 'ANNUAL' | 'ADJUST',
      remark: detail.plan.remark ?? '',
      items: detail.items.map((item) => ({
        key: item.id,
        deptId: item.deptId,
        deptName: item.deptName ?? '',
        categoryId: item.categoryId,
        categoryName: item.categoryName ?? '',
        materialId: item.materialId,
        materialName: item.materialName ?? '',
        lastYearAmount: item.lastYearAmount,
        applyAmount: item.applyAmount,
        calcBasis: item.calcBasis ?? ''
      }))
    });
    setDrawerOpen(true);
  };

  const total = draft.items.reduce((sum, item) => sum + (item.applyAmount ?? 0), 0);

  /** 明细行局部更新（`noUncheckedIndexedAccess` 下需先取到当前行再展开）。 */
  const patchItem = (index: number, patch: Partial<DraftItem>) =>
    setDraft((prev) => {
      const current = prev.items[index];
      if (!current) return prev;
      const items = [...prev.items];
      items[index] = { ...current, ...patch };
      return { ...prev, items };
    });

  const columns: ColumnsType<BudgetPlanRow> = [
    {
      title: '编制单号',
      dataIndex: 'bizNo',
      width: 200,
      render: (value: string, row) => (
        <Typography.Link onClick={() => (canManage ? openEdit(row) : undefined)}>{value}</Typography.Link>
      )
    },
    { title: '年度', dataIndex: 'budgetYear', width: 80 },
    { title: '版本', dataIndex: 'planVersion', width: 70, align: 'right' },
    {
      title: '类型',
      dataIndex: 'planType',
      width: 90,
      render: (value: string) => (value === 'ADJUST' ? '调整' : '年度')
    },
    {
      title: '申报总额',
      dataIndex: 'totalAmount',
      width: 140,
      align: 'right',
      render: (value: number) => formatAmount(value)
    },
    {
      title: '批复金额',
      dataIndex: 'approvedAmount',
      width: 140,
      align: 'right',
      render: (value: number) => formatAmount(value)
    },
    { title: '明细', dataIndex: 'itemCount', width: 70, align: 'right' },
    { title: '状态', dataIndex: 'status', width: 100, render: (value: string) => statusTag(value) },
    { title: '提交时间', dataIndex: 'submitAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_value, row) => {
        const editable = ['DRAFT', 'RETURNED', 'REJECTED'].includes(row.status);
        const canSubmit = editable;
        const canApprove = ['SUBMITTED', 'APPROVING'].includes(row.status);
        const canRelease = row.status === 'APPROVED';
        return (
          <Space size={4} wrap>
            {canManage && editable ? (
              <Button size="small" type="link" onClick={() => openEdit(row)}>
                编辑
              </Button>
            ) : null}
            {canManage && canSubmit ? (
              <Button size="small" type="link" loading={submit.isPending} onClick={() => submit.mutate(row.id)}>
                提交
              </Button>
            ) : null}
            {canManage && canApprove ? (
              <Button
                size="small"
                type="link"
                onClick={() => {
                  setApproveTarget(row);
                  setApproveAmount(row.totalAmount);
                  setApproveOpinion('');
                }}
              >
                批复
              </Button>
            ) : null}
            {canManage && canApprove ? (
              <Button
                size="small"
                type="link"
                danger
                onClick={() =>
                  modal.confirm({
                    title: '退回修改？',
                    content: '退回后由填报人修改并重新提交。',
                    okText: '退回',
                    cancelText: '取消',
                    onOk: () => reject.mutateAsync({ id: row.id, opinion: '退回修改' })
                  })
                }
              >
                退回
              </Button>
            ) : null}
            {canManage && canRelease ? (
              <Button
                size="small"
                type="link"
                loading={release.isPending}
                onClick={() =>
                  modal.confirm({
                    title: '下达正式预算？',
                    content: `将以批复金额 ${formatAmount(row.approvedAmount)} 写成本年度正式预算；同年度重复下达会覆盖金额。`,
                    okText: '下达',
                    cancelText: '取消',
                    onOk: () => release.mutateAsync(row.id)
                  })
                }
              >
                下达
              </Button>
            ) : null}
            {canManage && editable ? (
              <Button
                size="small"
                type="link"
                danger
                onClick={() =>
                  modal.confirm({
                    title: '删除预算编制单？',
                    content: '仅草稿/退回状态可删除。',
                    okText: '删除',
                    cancelText: '取消',
                    onOk: () => remove.mutateAsync(row.id)
                  })
                }
              >
                删除
              </Button>
            ) : null}
          </Space>
        );
      }
    }
  ];

  const itemColumns: ColumnsType<DraftItem> = [
    {
      title: '科室',
      dataIndex: 'deptId',
      width: 170,
      render: (_value, row, index) => (
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="选择科室"
          value={row.deptId || undefined}
          loading={depts.isPending}
          options={(depts.data ?? []).map((dept) => ({ value: dept.id, label: dept.deptName }))}
          onChange={(value) => {
            const dept = (depts.data ?? []).find((item) => item.id === value);
            patchItem(index, { deptId: value, deptName: dept?.deptName ?? '' });
          }}
        />
      )
    },
    {
      title: '品类',
      dataIndex: 'categoryId',
      width: 160,
      render: (_value, row, index) => (
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="可选"
          value={row.categoryId || undefined}
          loading={categories.isPending}
          options={(categories.data ?? []).map((item) => ({ value: item.id, label: item.categoryName }))}
          onChange={(value) => {
            const category = (categories.data ?? []).find((item) => item.id === value);
            patchItem(index, { categoryId: value ?? '', categoryName: category?.categoryName ?? '' });
          }}
        />
      )
    },
    {
      title: '物资名称（可选）',
      dataIndex: 'materialName',
      width: 160,
      render: (_value, row, index) => (
        <Input
          value={row.materialName}
          placeholder="不填则为品类级预算"
          onChange={(event) => patchItem(index, { materialName: event.target.value })}
        />
      )
    },
    {
      title: '上年执行额',
      dataIndex: 'lastYearAmount',
      width: 130,
      align: 'right',
      render: (_value, row, index) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={row.lastYearAmount}
          onChange={(next) => patchItem(index, { lastYearAmount: typeof next === 'number' ? next : 0 })}
        />
      )
    },
    {
      title: '申报金额',
      dataIndex: 'applyAmount',
      width: 140,
      align: 'right',
      render: (_value, row, index) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={row.applyAmount}
          onChange={(next) => patchItem(index, { applyAmount: typeof next === 'number' ? next : 0 })}
        />
      )
    },
    {
      title: '计算依据',
      dataIndex: 'calcBasis',
      width: 180,
      render: (_value, row, index) => (
        <Input
          value={row.calcBasis}
          placeholder="如：上年执行 × 1.05"
          onChange={(event) => patchItem(index, { calcBasis: event.target.value })}
        />
      )
    },
    {
      title: '',
      key: 'action',
      width: 60,
      render: (_value, row) => (
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={draft.items.length <= 1}
          onClick={() => setDraft((prev) => ({ ...prev, items: prev.items.filter((item) => item.key !== row.key) }))}
        />
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          预算编制
          <Typography.Text type="secondary">填报 → 提交 → 批复 → 下达，下达后进入预算执行口径</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <InputNumber
            min={2000}
            max={2100}
            value={year}
            onChange={(value) => setYear(typeof value === 'number' ? value : dayjs().year())}
            style={{ width: 100 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!canManage} onClick={openCreate}>
            新建编制
          </Button>
        </Space>
      }
    >
      <Segmented<StatusFilter>
        value={status}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '草稿', value: 'DRAFT' },
          { label: '待批复', value: 'SUBMITTED' },
          { label: '已批复', value: 'APPROVED' },
          { label: '已下达', value: 'RELEASED' }
        ]}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="预算编制单加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<BudgetPlanRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={list.data?.items ?? []}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '本年度暂无预算编制单' }}
      />

      <Drawer
        title={editing ? `编辑 ${editing.bizNo}` : '新建预算编制'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={1100}
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={save.isPending} onClick={() => save.mutate()}>
              保存
            </Button>
          </Space>
        }
      >
        {editing ? (
          <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="状态">{statusTag(editing.status)}</Descriptions.Item>
            <Descriptions.Item label="申报总额">{formatAmount(editing.totalAmount)}</Descriptions.Item>
            <Descriptions.Item label="批复金额">{formatAmount(editing.approvedAmount)}</Descriptions.Item>
          </Descriptions>
        ) : null}

        <Form layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item label="年度">
            <InputNumber
              min={2000}
              max={2100}
              value={draft.budgetYear}
              onChange={(value) => setDraft((prev) => ({ ...prev, budgetYear: typeof value === 'number' ? value : prev.budgetYear }))}
            />
          </Form.Item>
          <Form.Item label="类型">
            <Select
              style={{ width: 120 }}
              value={draft.planType}
              options={[
                { value: 'ANNUAL', label: '年度' },
                { value: 'ADJUST', label: '调整' }
              ]}
              onChange={(value) => setDraft((prev) => ({ ...prev, planType: value }))}
            />
          </Form.Item>
          <Form.Item label="备注">
            <Input
              style={{ width: 320 }}
              value={draft.remark}
              maxLength={128}
              onChange={(event) => setDraft((prev) => ({ ...prev, remark: event.target.value }))}
            />
          </Form.Item>
        </Form>

        <Space style={{ marginBottom: 12 }}>
          <Typography.Text strong>明细（申报合计 {formatAmount(total)}）</Typography.Text>
          <Button size="small" icon={<PlusOutlined />} onClick={() => setDraft((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }))}>
            添加行
          </Button>
        </Space>

        <Table<DraftItem>
          rowKey="key"
          size="small"
          columns={itemColumns}
          dataSource={draft.items}
          pagination={false}
          scroll={{ x: sumWidth(itemColumns) }}
        />
      </Drawer>

      <Modal
        title={`批复 ${approveTarget?.bizNo ?? ''}`}
        open={approveTarget !== null}
        onCancel={() => setApproveTarget(null)}
        okText="批复"
        cancelText="取消"
        confirmLoading={approve.isPending}
        onOk={() => approve.mutate()}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`申报总额 ${formatAmount(approveTarget?.totalAmount ?? 0)}`}
          description="批复金额低于申报总额时，明细按比例等比调减；批复后需执行「下达」才写入正式预算。"
        />
        <Form layout="vertical">
          <Form.Item label="批复金额">
            <InputNumber
              min={0}
              max={approveTarget?.totalAmount ?? 0}
              precision={2}
              style={{ width: '100%' }}
              value={approveAmount}
              onChange={(value) => setApproveAmount(typeof value === 'number' ? value : 0)}
            />
          </Form.Item>
          <Form.Item label="批复意见">
            <Input.TextArea rows={3} maxLength={200} value={approveOpinion} onChange={(event) => setApproveOpinion(event.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
